// Moteur Agent IA local — interroge les données du site sans LLM.
// Retourne une réponse markdown structurée à partir de la question.

import { PCG_INDEX, NATURE_LABELS, SENS_LABELS } from "@/lib/pcgUtils";
import { PCG } from "@/data/pcg";
import { GLOSSAIRE_INDEX } from "@/data/glossaire";
import { ECRITURES, TOTAL_TVA } from "@/data/ecritures";
import { searchEcritures } from "@/lib/casUsage";

function norm(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function extractAccountNums(q) {
  return Array.from(new Set((q.match(/\b\d{2,5}\b/g) || [])));
}

function scoreAccount(query, account) {
  const nq = norm(query);
  const nlib = norm(account.lib);
  let s = 0;
  const tokens = nq.split(/\s+/).filter((t) => t.length > 2);
  for (const t of tokens) {
    if (t.match(/^\d+$/)) {
      if (account.num === t) s += 100;
      else if (account.num.startsWith(t)) s += 30;
    } else if (nlib.includes(t)) {
      s += 12;
    }
  }
  // Bonus comptes courts
  if (account.num.length <= 3) s += 2;
  return s;
}

function searchGlossaire(query, k = 3) {
  const nq = norm(query);
  const tokens = nq.split(/\s+/).filter((t) => t.length > 2);
  const scored = GLOSSAIRE_INDEX.map((g) => {
    let s = 0;
    const nt = norm(g.terme);
    const nd = norm(g.def);
    for (const t of tokens) {
      if (nt === t) s += 50;
      else if (nt.includes(t) || t.includes(nt)) s += 20;
      else if (nd.includes(t)) s += 4;
    }
    return { score: s, item: g };
  })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, k).map((x) => x.item);
}

function searchAccounts(query, k = 5) {
  const nums = extractAccountNums(query);
  // Direct hits
  const direct = nums.map((n) => PCG_INDEX[n]).filter(Boolean);
  // Sémantique
  const semantic = PCG.map((a) => ({ score: scoreAccount(query, a), a }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((x) => x.a);
  const seen = new Set();
  const out = [];
  for (const a of [...direct, ...semantic]) {
    if (!seen.has(a.num)) {
      seen.add(a.num);
      out.push(a);
      if (out.length >= k) break;
    }
  }
  return out;
}

function detectIntent(query) {
  const nq = norm(query);
  if (/\b(qu['e ]?est|definition|signifie|c['e ]?est quoi|que veut dire)\b/.test(nq)) return "glossaire";
  if (/\b(diff[eé]rence|vs|versus|comparer|comparaison)\b/.test(nq)) return "compare";
  if (/\b(comptabilis|passer|ecriture|j['' ]?ai|je veux|comment ecr|comment fact|vendu|achete|achat|vente|paye|paie|encaiss|regle)\b/.test(nq)) return "ecriture";
  if (/\b\d{2,5}\b/.test(query)) return "compte";
  return "general";
}

const fmt = (n) =>
  n === 0 ? "—" : n.toLocaleString("fr-FR", { minimumFractionDigits: 2 });

function ecritureToMarkdown(ec) {
  const total = TOTAL_TVA(ec.lignes);
  const rows = ec.lignes
    .map((l) => `| \`${l.num}\` | ${l.lib} | ${fmt(l.debit)} | ${fmt(l.credit)} |`)
    .join("\n");
  return `**Situation :** _${ec.situation}_

| N° | Libellé | Débit | Crédit |
|---|---|---:|---:|
${rows}
| | **Totaux** | **${fmt(total.debit)}** | **${fmt(total.credit)}** |

✅ Écriture équilibrée.

**Pourquoi :** ${ec.commentaire}`;
}

function accountToMarkdown(a) {
  const nature = NATURE_LABELS[a.nature]?.label || "—";
  const sens = SENS_LABELS[a.sens]?.label || "—";
  const lies = (a.lies || []).slice(0, 3).map((l) => `\`${l.n}\``).join(", ");
  return `### Compte \`${a.num}\` — ${a.lib}

**Classe ${a.classe}** · Nature : *${nature}* · Sens normal : *${sens}*

${a.usage || ""}

- **Quand est-il débité ?** ${a.fonctionnement?.debit || "—"}
- **Quand est-il crédité ?** ${a.fonctionnement?.credit || "—"}
${lies ? `\n**Comptes à ne pas confondre :** ${lies}` : ""}`;
}

function glossaireToMarkdown(g) {
  return `### ${g.terme}\n*${g.cat}*\n\n${g.def}${g.ex ? `\n\n_Exemple :_ ${g.ex}` : ""}`;
}

// ─── Réponse principale ──────────────────────────────────────────────────
export function answerLocal(query) {
  const q = query.trim();
  if (!q) return { md: "Pose-moi une question !", sources: [] };
  const intent = detectIntent(q);
  const sources = [];

  let intro = "";
  let body = [];

  if (intent === "glossaire") {
    const terms = searchGlossaire(q, 3);
    if (terms.length === 0) {
      return {
        md: `Je n'ai pas trouvé ce terme dans mon glossaire (31 entrées). Essaie une autre formulation, ou consulte la rubrique **Glossaire** pour explorer.`,
        sources: [],
      };
    }
    intro = `Voici ce que je trouve dans le **glossaire KEYTO** :`;
    body = terms.map(glossaireToMarkdown);
    sources.push(...terms.map((t) => ({ type: "glossaire", label: t.terme })));
  } else if (intent === "compte") {
    const accs = searchAccounts(q, 3);
    if (accs.length === 0) {
      return {
        md: `Je n'ai pas trouvé de compte correspondant. Vérifie le numéro ou décris l'opération en langage naturel.`,
        sources: [],
      };
    }
    intro = accs.length === 1 ? `Voici la fiche du compte :` : `Voici les comptes qui correspondent :`;
    body = accs.map(accountToMarkdown);
    sources.push(...accs.map((a) => ({ type: "compte", num: a.num, label: a.lib })));
  } else if (intent === "compare") {
    const accs = searchAccounts(q, 4);
    if (accs.length < 2) {
      return {
        md: `Pour comparer, donne-moi au moins 2 numéros de compte (ex : « différence entre 411 et 416 »).`,
        sources: [],
      };
    }
    intro = `Comparaison côte à côte :`;
    body = [
      `| Critère | ${accs.map((a) => `\`${a.num}\``).join(" | ")} |`,
      `|---|${accs.map(() => "---").join("|")}|`,
      `| Libellé | ${accs.map((a) => a.lib).join(" | ")} |`,
      `| Classe | ${accs.map((a) => a.classe).join(" | ")} |`,
      `| Nature | ${accs.map((a) => NATURE_LABELS[a.nature]?.label || "—").join(" | ")} |`,
      `| Sens normal | ${accs.map((a) => SENS_LABELS[a.sens]?.label || "—").join(" | ")} |`,
      ``,
      ...accs.map((a) => `- **\`${a.num}\`** : ${a.usage || "—"}`),
    ];
    sources.push(...accs.map((a) => ({ type: "compte", num: a.num, label: a.lib })));
  } else if (intent === "ecriture") {
    const schemas = searchEcritures(q).slice(0, 2);
    if (schemas.length === 0) {
      // Fallback : suggère quelques comptes pertinents
      const accs = searchAccounts(q, 3);
      if (accs.length === 0) {
        return {
          md: `Je n'ai pas trouvé d'écriture toute prête pour cette situation. Essaie de reformuler avec un verbe d'action (acheter, vendre, payer, encaisser, déclarer…) ou consulte la rubrique **Schémas d'écritures** (24 modèles).`,
          sources: [],
        };
      }
      intro = `Pas de schéma exact, mais ces comptes me semblent pertinents :`;
      body = accs.map(accountToMarkdown);
      sources.push(...accs.map((a) => ({ type: "compte", num: a.num, label: a.lib })));
    } else {
      intro =
        schemas.length === 1
          ? `Voici le schéma d'écriture qui correspond :`
          : `J'ai ${schemas.length} schémas qui pourraient correspondre :`;
      body = schemas.map(
        (ec) => `### ${ec.titre} _(cycle : ${ec.cycle})_\n\n${ecritureToMarkdown(ec)}`
      );
      sources.push(...schemas.map((ec) => ({ type: "ecriture", id: ec.id, label: ec.titre })));
    }
  } else {
    // général : essaie tout
    const schemas = searchEcritures(q).slice(0, 2);
    const accs = searchAccounts(q, 3);
    const terms = searchGlossaire(q, 2);

    if (schemas.length === 0 && accs.length === 0 && terms.length === 0) {
      return {
        md: `Je n'ai pas de réponse précise dans ma base. Quelques pistes :

- Cite un **numéro de compte** (ex : « 411 », « 44571 »)
- Décris l'**opération** (ex : « j'ai vendu un service », « cession d'immobilisation »)
- Demande la **définition** d'un terme (ex : « qu'est-ce que le BFR »)

Tu peux aussi explorer les rubriques **Schémas d'écritures**, **Glossaire**, ou **Comparateur** depuis la barre latérale.`,
        sources: [],
      };
    }
    intro = `Voici ce que je trouve sur cette question :`;
    if (schemas.length) {
      body.push(
        ...schemas.map(
          (ec) => `### ${ec.titre} _(cycle : ${ec.cycle})_\n\n${ecritureToMarkdown(ec)}`
        )
      );
      sources.push(...schemas.map((ec) => ({ type: "ecriture", id: ec.id, label: ec.titre })));
    }
    if (accs.length) {
      body.push(`### Comptes pertinents\n`);
      body.push(...accs.slice(0, 2).map(accountToMarkdown));
      sources.push(...accs.slice(0, 2).map((a) => ({ type: "compte", num: a.num, label: a.lib })));
    }
    if (terms.length) {
      body.push(...terms.map(glossaireToMarkdown));
      sources.push(...terms.map((t) => ({ type: "glossaire", label: t.terme })));
    }
  }

  return { md: `${intro}\n\n${body.join("\n\n---\n\n")}`, sources };
}
